import { useMutation, useQuery } from '@tanstack/vue-query';
import {
  type UpdateYieldManagementSettingsDto,
  type YieldManagementSettingsControllerCalculatePricePerExtraKmData,
  type YieldManagementSettingsControllerGetDepositData,
  type YieldManagementSettingsControllerGetMinAgeData,
} from '@carvia/ros-client-types';
import type { MaybeRefOrGetter } from '@vueuse/core';
import { yieldManagementSettingsService } from '@/api/yield-management-settings.service.ts';
import { toValue } from 'vue';
import type { MutationDefaultsContext } from '@/queries/query-client.ts';

const QUERY_KEY = 'yield-management-settings';

export const useYieldManagementSettings = () => {
  return useQuery({
    queryKey: [QUERY_KEY],
    queryFn: () => yieldManagementSettingsService.getYieldManagementSettings(),
  });
};

export const useUpdateYieldManagementSettings = () => {
  return useMutation({
    mutationKey: [QUERY_KEY],
    mutationFn: (settings: UpdateYieldManagementSettingsDto) =>
      yieldManagementSettingsService.updateYieldManagementSettings(settings),
    onMutate: (): MutationDefaultsContext => ({
      successMessage: 'Yield management settings updated',
      invalidateQueryKeys: [QUERY_KEY],
    }),
  });
};

export const useCalculatePricePerExtraKm = (
  query: MaybeRefOrGetter<YieldManagementSettingsControllerCalculatePricePerExtraKmData['query']>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useQuery({
    queryKey: ['calculate-price-per-extra-km', query],
    queryFn: () => yieldManagementSettingsService.calculatePricePerExtraKm(toValue(query)),
    enabled,
  });
};

export const useGetDeposit = (
  query: MaybeRefOrGetter<YieldManagementSettingsControllerGetDepositData['query']>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useQuery({
    queryKey: ['get-deposit', query],
    queryFn: () => yieldManagementSettingsService.getDeposit(toValue(query)),
    enabled,
  });
};

export const useGetMinAge = (
  query: MaybeRefOrGetter<YieldManagementSettingsControllerGetMinAgeData['query']>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useQuery({
    queryKey: ['get-min-age', query],
    queryFn: () => yieldManagementSettingsService.getMinAge(toValue(query)),
    enabled,
  });
};
